// import { action } from '@ember/object';
import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { next, schedule } from '@ember/runloop';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import type { BlockThemeConfig, Service } from 'webapp-types';
import { ReportSlideFragmentType } from 'webapp/enums/enums';

import didUpdate from 'webapp/modifiers/did-update';
import BlockSingleComponent from 'webapp/components/block/single';
import ReportsSkeletonFragmentComponent from 'webapp/components/reports/skeleton-fragment';

import type Block from 'webapp/models-custom/block';
import BoardState from 'webapp/models-custom/board-state';
import type ReportSlideFragment from 'webapp/models-custom/report-slide-fragment';

export interface Args {
	model: ReportSlideFragment;
	isEditable?: boolean;
	onBlockReady?: () => void;
	onClick?: () => void; // reports editor side-by-side
}

interface ReportsViewerBlockCtComponentInterface<T> {
	Args: T;
	Blocks: { default: [] };
}

export default class ReportsViewerBlockCtComponent extends Component<
	ReportsViewerBlockCtComponentInterface<Args>
> {
	@service('model-manager/blocks') declare mmBlocksService: Service.ModelManager.Blocks;
	@service('constants') declare constantsService: Service.Constants;
	@service('navigation') declare navigationService: Service.Navigation;

	@tracked boardState: BoardState | null = null;
	@tracked block: Block | null = null; // just to avoid rendering issues
	@tracked isBoardStateReady = false; // just to avoid rendering issues

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		this.setupBoardState();
	}

	get colorConfig(): BlockThemeConfig.Parsed {
		const fragment = this.args.model;
		if (fragment.isTypeBlock) {
			return fragment.blockColorConfig;
		}

		if (this.args.model.isOnPagelessReport) {
			// is in viewer? if so, use white theme
			const hasReportViewerBody = document.querySelector('#report-viewer_body') !== null;
			const isDark = !hasReportViewerBody && this.navigationService.theme.isDark;
			const bgColor = isDark ? '#000000' : '#ffffff';
			const colorConfig = this.args.model.getColorConfig(bgColor, 1);
			return colorConfig;
		}

		return fragment.slide!.colorConfig;
	}

	get skeletonType(): ReportSlideFragmentType {
		return this.args.model.type;
	}

	checkBoardState(): void {
		// conditional model bcos we run this after requestBlockData promise
		this.block = this.args.model?.block;
		if (this.block && !this.isBoardStateReady) {
			const boardState = BoardState.createForSingleBlock(this.block);
			boardState.explicitlyDisableAllPreviews = !this.args.isEditable;
			boardState.isViewOnly = !this.args.isEditable;
			boardState.onStateChange = (changesPayload) => {
				// save all changes directly
				if (changesPayload.blockPreviews) {
					this.mmBlocksService.updateBlocksOnBoardStateChange(
						changesPayload.blockPreviews,
					);
				}
			};

			this.boardState = boardState;
			this.isBoardStateReady = true;
			this.args.model.isNewlyCreated = false;
		}
	}

	@action
	setupBoardState(): void {
		const fragmentModel = this.args.model;
		if (this.boardState?.selectedBlock?.numericId !== fragmentModel.blockId) {
			this.isBoardStateReady = false;
			schedule('afterRender', () => {
				next(() => {
					void fragmentModel.requestBlockData().then(() => {
						this.checkBoardState();
						this.args.onBlockReady?.();
					});
				});
			});
		}
	}

	// @action
	// doNothing(): void {
	// 	// READ THE METHOD NAME
	// }
	<template>
		{{#if this.isBoardStateReady}}

			{{#if this.boardState}}
				<BlockSingleComponent
					@boardState={{this.boardState}}
					@colorConfig={{this.colorConfig}}
					@showBg={{true}}
					@extraClasses={{unless this.block.sample "loading"}}
					@onClick={{@onClick}}
				>
					{{yield}}
				</BlockSingleComponent>
			{{/if}}

		{{else}}
			{{! https://app.asana.com/0/1138028810573038/1208881471732241/f }}
			{{#unless @model.isNewlyCreated}}
				<ReportsSkeletonFragmentComponent @type={{this.skeletonType}} />
			{{/unless}}
		{{/if}}

		<span {{didUpdate this.setupBoardState @model.block}}></span>
	</template>
}
